import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLayout } from '../context/LayoutContext';

function DebugModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [, setKeyBuffer] = useState('');
  const { currentLayout, config, screenSize } = useLayout();

  // Listen for "DEBUG" typed anywhere
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only track letter keys
      if (e.key.length === 1 && e.key.match(/[a-zA-Z]/)) {
        setKeyBuffer(prev => {
          const newBuffer = (prev + e.key.toUpperCase()).slice(-5);
          if (newBuffer === 'DEBUG') {
            setIsOpen(true);
            return '';
          }
          return newBuffer;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex items-center justify-center bg-[var(--color-scrim)] backdrop-blur-sm">
      <div className="bg-[var(--color-surface-elevated)] rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-divider)]">
          <h2 className="text-xl font-bold text-[var(--color-ink-main)]">Debug Info</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-[var(--color-surface-input)] rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {/* Screen Info */}
            <div className="bg-[var(--color-surface-input)] rounded-lg p-4">
              <h3 className="font-semibold text-[var(--color-ink-main)] mb-2">Screen</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-[var(--color-ink-subtle)]">Width:</div>
                <div className="font-mono">{screenSize?.width || window.innerWidth}px</div>
                <div className="text-[var(--color-ink-subtle)]">Height:</div>
                <div className="font-mono">{screenSize?.height || window.innerHeight}px</div>
                <div className="text-[var(--color-ink-subtle)]">Orientation:</div>
                <div className="font-mono">{window.innerWidth > window.innerHeight ? 'Landscape' : 'Portrait'}</div>
                <div className="text-[var(--color-ink-subtle)]">Pixel Ratio:</div>
                <div className="font-mono">{window.devicePixelRatio}</div>
              </div>
            </div>

            {/* Layout Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-semibold text-blue-700 mb-2">Current Layout</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-blue-500">Layout Type:</div>
                <div className="font-mono font-bold text-blue-900">{currentLayout}</div>
                <div className="text-blue-500">Week View Layout:</div>
                <div className="font-mono">{config?.components?.weekView?.layout || 'N/A'}</div>
                <div className="text-blue-500">Button Size:</div>
                <div className="font-mono">{config?.components?.weekView?.buttonSize || 'N/A'}</div>
              </div>
            </div>

            {/* Config Details */}
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold text-green-700 mb-2">Week View Config</h3>
              <pre className="text-xs font-mono bg-[var(--color-surface-card)] p-3 rounded border border-[var(--color-divider)] overflow-x-auto">
                {JSON.stringify(config?.components?.weekView, null, 2)}
              </pre>
            </div>

            {/* Breakpoints */}
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-semibold text-purple-700 mb-2">Breakpoints</h3>
              <div className="text-sm space-y-1">
                <div className={`flex justify-between ${window.innerWidth < 768 ? 'font-bold text-purple-900' : 'text-purple-600'}`}>
                  <span>Mobile (&lt;768px)</span>
                  <span>{window.innerWidth < 768 ? '← Current' : ''}</span>
                </div>
                <div className={`flex justify-between ${window.innerWidth >= 768 && window.innerWidth < 1280 ? 'font-bold text-purple-900' : 'text-purple-600'}`}>
                  <span>Tablet (768-1280px)</span>
                  <span>{window.innerWidth >= 768 && window.innerWidth < 1280 ? '← Current' : ''}</span>
                </div>
                <div className={`flex justify-between ${window.innerWidth >= 1280 ? 'font-bold text-purple-900' : 'text-purple-600'}`}>
                  <span>Desktop (≥1280px)</span>
                  <span>{window.innerWidth >= 1280 ? '← Current' : ''}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--color-divider)] bg-[var(--color-surface-input)]">
          <p className="text-xs text-[var(--color-ink-subtle)] text-center">
            Type "DEBUG" anywhere to open this modal again
          </p>
        </div>
      </div>
    </div>
  );
}

export default DebugModal;
